import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, ViewChild } from '@angular/core';
import { interval, Observable, tap } from 'rxjs';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartUtils } from '../../utils';

@Component({
    selector: 'ca-chart-placeholder',
    templateUrl: './chart-placeholder.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChartPlaceholderComponent implements OnInit {
    @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
    private readonly destroyRef = inject(DestroyRef);
    ticksCallback = input<(value: string | number) => string>();
    valueUnit = input<string | null>(null);
    locale = input.required<string>();
    labels = input.required<string[]>();
    datasetLabels = input.required<string[]>();
    values = computed(() => this.labels().map(() => 35000));
    chartHeight = computed(() => this.labels().length * 50 + 120);
    protected placeholderChartConfiguration: ChartConfiguration;
    private gradientPosition = 0;
    private placeholderChartAnimation$: Observable<number> = interval(40).pipe(
        tap(() => {
            this.gradientPosition = (this.gradientPosition + 0.05) % 1;
            this.chart!.update();
        }),
        takeUntilDestroyed(this.destroyRef)
    );

    ngOnInit(): void {
        this.initPlaceholderChart();
        this.placeholderChartAnimation$.subscribe();
    }

    private initPlaceholderChart(): void {
        this.placeholderChartConfiguration = {
            type: 'bar',
            data: {
                labels: this.labels(),
                datasets: this.datasetLabels().map(label => this.getDataSet(label))
            },
            options: {
                indexAxis: 'y',
                maintainAspectRatio: false,
                animations: {
                    x: { duration: 0 },
                    y: { duration: 0 }
                },
                plugins: {
                    tooltip: { enabled: false },
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: ChartUtils.labels
                    }
                },
                scales: {
                    y: {
                        offset: true,
                        grid: { display: false },
                        ticks: {
                            ...ChartUtils.ticks,
                            callback: (_, index: number) => {
                                const label = (this.placeholderChartConfiguration.data?.labels as string[])[index];
                                return ChartUtils.splitWideLabelToTwoLinesIfPossible(label);
                            }
                        }
                    },
                    x: {
                        min: 10000,
                        grid: {
                            display: true,
                            drawTicks: false
                        },
                        ticks: {
                            ...ChartUtils.ticks,
                            callback: value => {
                                if (this.ticksCallback() !== undefined) {
                                    return this.ticksCallback()!(value);
                                }
                                if (typeof value === 'number') {
                                    value = value.toLocaleString(this.locale());
                                }
                                return this.valueUnit() ? `${value} ${this.valueUnit()}` : value;
                            }
                        }
                    }
                }
            }
        };
    }

    private getDataSet(label: string): ChartDataset<'bar', number[]> {
        return {
            label: ` ${label}`,
            type: 'bar',
            data: this.values(),
            backgroundColor: context => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;
                if (!chartArea) {
                    return '#e3e3e3';
                }
                const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
                gradient.addColorStop(0, '#e3e3e3');
                gradient.addColorStop(this.gradientPosition, '#fbfbfb');
                gradient.addColorStop(1, '#e3e3e3');
                return gradient;
            },
            barThickness: 50,
            grouped: false
        };
    }
}
